import { Accordion } from "@trackman/web-shared-components";
import { AppButton } from "components/form/AppButton";
import { AppSpacer } from "components/ui/AppSpacer";
import { AppChip } from "components/ui/Chips/AppChip";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { DefaultRoutes } from "Routes";
import { ITrackingDiagnostic } from "types/models/TrackingDiagnosticsModel";
import { getTrackingDiagnosticsTypeColor } from "utilities/SystemUtils";

interface ITrackingDiagnosticsAccordionProps {
    entryIds: string[];
    onRemove: (id: string) => void;
    hideGotoButton?: boolean;
    hideRemoveButton?: boolean;
}

export const TrackingDiagnosticsAccordion = observer(({ entryIds, onRemove, hideGotoButton, hideRemoveButton }: ITrackingDiagnosticsAccordionProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { trackingDiagnosticsStore } = useStores();

    const [expandedEntriesAcordion, setExpandedEntriesAcordion] = useState<boolean>(false);
    const [foundTrackingDiagnostics, setFoundTrackingDiagnostics] = useState<ITrackingDiagnostic[] | null>(null);

    useEffect(() => {
        const trackingDiagnostics = trackingDiagnosticsStore.trackingDiagnostics.filter(x => entryIds.includes(x.id));
        setFoundTrackingDiagnostics(trackingDiagnostics);
    }, [entryIds]);

    const renderFileEntryContent = (entry: ITrackingDiagnostic) => {
        const classStyling = 'font-10 font-700 font-lato text-capitalize color-secondaryGrey mb-0'

        return (
            <div className='d-flex flex-row my-3'>
                <p className={classStyling}>{entry.id}</p>
                <AppSpacer isHorizontal space='1rem' />
                <AppChip<string> value={entry?.type ?? null} getColor={() => getTrackingDiagnosticsTypeColor(entry?.type ?? null)} />
                <AppSpacer isHorizontal space='1rem' />
                <p className={classStyling}>{entry.organizationName}</p>
            </div>
        );
    };

    const handleGotoTrackingDiagnostics = useCallback((id: string) => {
        history.push({
            pathname: `${DefaultRoutes.TrackingDiagnosticsPage}/`,
            search: `?selectedTrackingDiagnostic=${id}`
        });
    }, []);

    return (
        <Accordion
            expanded={expandedEntriesAcordion}
            paperProps={{ className: 'mb-3' }}
            severity='none'
            onChange={(e) => {
                setExpandedEntriesAcordion(!expandedEntriesAcordion);
            }}
            title={!foundTrackingDiagnostics ? `-` : `${t('DiagnosticsPackages.Section.Accordion.TrackingDiagnostics', { amount: `${foundTrackingDiagnostics.length}` })}`}
        >
            <ul className="d-flex flex-column my-3 align-items-stretch">
                {foundTrackingDiagnostics?.map((entry, index) => (
                    <li key={index} className='d-flex align-items-center justify-content-between w-100 mb-4'>
                        {renderFileEntryContent(entry)}
                        <div className='d-flex flex-row my-3'>
                            {!hideGotoButton && (
                                <AppButton
                                    variant='primary'
                                    className='border-primary'
                                    onClick={(e) => {
                                        e?.stopPropagation();
                                        handleGotoTrackingDiagnostics(entry.id);
                                    }}
                                >
                                    {t('DiagnosticsPackages.Action.Goto').toString()}
                                </AppButton>)}
                            <AppSpacer isHorizontal space='1rem' />
                            {!hideRemoveButton && foundTrackingDiagnostics.length > 1 && (
                                <AppButton
                                    variant='warning'
                                    className='border-warning'
                                    onClick={(e) => {
                                        e?.stopPropagation();
                                        onRemove(entry.id);
                                    }}
                                >
                                    {t('DiagnosticsPackages.Action.Remove').toString()}
                                </AppButton>)
                            }
                        </div>
                    </li>
                ))}
            </ul>
        </Accordion>
    )
});

export default TrackingDiagnosticsAccordion;